import { StyledLink } from "orcs-design-system";
import PropTypes from "prop-types";
import React from "react";
import styled from "styled-components";

import { useOrgBranding } from "src/hooks/orgConfig/hooks/useOrgBranding";
import useDefaultRoutePath from "src/app/Restricted/useDefaultRoutePath";

const LogoLink = styled(StyledLink)`
  line-height: 0;
`;

const CustomLogo = styled.img`
  /* Workspace/tenant logos are typically authored for a light header. In dark
     mode, invert lightness while preserving hue (invert + hue-rotate) so the
     logo stays visible instead of going dark-on-dark. Admin-provided styles are
     applied afterwards and can override this if a dark-ready logo is supplied. */
  ${(props) =>
    props.theme?.isDark ? "filter: invert(1) hue-rotate(180deg);" : ""}
  ${(props) => props.styles}
`;

const Logo = ({ workspace }) => {
  const defaultRoutePath = useDefaultRoutePath();
  const {
    branding: { logoImage },
  } = useOrgBranding();

  if (workspace && !workspace.loading && logoImage?.src) {
    // Admins configure the logo image alt text via the Branding admin page
    // (e.g. "Acme Corp"). Keep that on the <img> so it describes the image
    // itself, and compose it into the link's aria-label so the link's
    // accessible name also conveys the action ("Acme Corp - go to home page").
    // When no brand alt is set, fall back to a generic image description and
    // a plain purpose-based link label so the link always has an accessible
    // name (WCAG link-name).
    const brandName = logoImage.alt?.trim();
    const imageAlt = brandName || "Company logo";
    const linkLabel = brandName
      ? `${brandName} - go to home page`
      : "Go to home page";
    return (
      <LogoLink
        bold
        white
        to={defaultRoutePath}
        data-testid="directory-logo-link"
        aria-label={linkLabel}
      >
        <CustomLogo {...logoImage} alt={imageAlt} />
      </LogoLink>
    );
  }

  return null;
};

Logo.propTypes = {
  workspace: PropTypes.object,
};

export default Logo;
